import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useModal, Flex, Grid, Box, Text } from 'vvs-uikit'
import { useFeedFoodMutation } from 'vvs-gotchi-graphql'
import useToast from 'hooks/useToast'
import { useGotchiAuth } from 'state/vvsgotchi/hooks/useAuth'
import useGotchiOwner from 'state/vvsgotchi/hooks/useGotchiOwner'
import { Mole, FoodType, foodTypeToGraphqlMap } from 'state/vvsgotchi/models/mole'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useTranslation } from 'contexts/Localization'
import { toastTypes } from 'components/Toast'
import { RowBetween } from 'components/Layout/Row'
import { CloseButton } from 'components/CloseButton'
import PixelatedImage from 'views/VvsGotchi/components/PixelatedImage'
import { MoleMiniCard, MoleMiniCardShadow } from 'views/VvsGotchi/components/MoleCard'
import CheckBox from 'views/VvsGotchi/components/CheckBox'
import ActionButton from 'views/VvsGotchi/components/ActionButton'
import ScreenFooter from 'views/VvsGotchi/components/ScreenFooter'
import { RunningModal } from 'views/VvsGotchi/components/Modal'
import { Path } from 'config/constants/paths'
import waitPromise from 'utils/waitPromise'
import { foodTypeToUiDetails } from 'views/VvsGotchi/config'
import historyBackOrReplace from 'utils/historyBackOrReplace'
import { useVvsGotchiAudioModeManager } from 'state/vvsgotchi/hooks/useConfigManager'
import * as ga from 'utils/googleAnalyticsHelper'
import { DarkPageContainer, FullScreenStackContentFlex, StackContentFlex } from '../styles'
import { OptionFlex, DetailText } from './styles'
import successSound from '../../../assets/feed_success.mp3'
import failSound from '../../../assets/feed_fail.mp3'

const feedSuccessSound = new Audio(successSound)
const feedFailSound = new Audio(failSound)

const FeedMole: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { toastVVSGotchi } = useToast()
  const { account } = useActiveWeb3React()

  const { team, loading, refresh } = useGotchiOwner()
  const [, ensureAuth] = useGotchiAuth()

  const { sePlay } = useVvsGotchiAudioModeManager()

  const [feedWholeTeam, setFeedWholeTeam] = useState(true)
  const [excludedTokenIds, setExcludedTokenIds] = useState<number[]>([])
  const [foodType, setFoodType] = useState(FoodType.FRIES)

  const [feedFood] = useFeedFoodMutation()

  const [openFeedingModal, closeFeedingModal] = useModal(
    <RunningModal imageSrc={foodTypeToUiDetails[foodType].feedingImage}>{t('Feeding...')}</RunningModal>,
    false,
    false,
    'vvsgotchiFeeding',
  )

  if (isNoMoleInTeam(team)) {
    return (
      <DarkPageContainer popup>
        <Flex justifyContent="space-between" my={4} mx={3}>
          <Text fontSize="20px" fontWeight="600" color="white">
            {t('Food')}
          </Text>
          <CloseButton onClick={() => historyBackOrReplace(history, Path.VVS_GOTCHI_TEAM)} />
        </Flex>
        <Flex flexDirection="column" justifyContent="center" alignItems="center" my={5} mx={4}>
          {loading ? (
            <>
              <Text fontSize="20px" fontWeight="600" color="white">
                {t('Loading...')}
              </Text>
            </>
          ) : (
            <>
              <Text fontSize="20px" fontWeight="600" color="white">
                {t('( ˘︹˘ )')}
              </Text>
              <Text fontSize="20px" fontWeight="600" color="white">
                {t('No Moles to Feed')}
              </Text>
              <Text textAlign="center" color="white">
                {t('Add Miner Moles to the Team but make sure to take care of them!')}
              </Text>
            </>
          )}
        </Flex>
        <ActionButton mt={4} disabled={loading} onClick={() => history.replace(Path.VVS_GOTCHI_TEAM_SELECT)}>
          {t('Add Miner Moles')}
        </ActionButton>
      </DarkPageContainer>
    )
  }

  const fedMoles = team?.filter((m) => m.hasFedToday)
  const individuallyOnly = !team || team?.length <= 1 || fedMoles.length > 0
  const feedIndividually = individuallyOnly || !feedWholeTeam
  const selectedFoodDetails = foodTypeToUiDetails[foodType]

  return (
    <DarkPageContainer background="darkestNightBlue">
      <Flex justifyContent="space-between" my={4} mx={3}>
        <Text fontSize="20px" fontWeight="600" color="white">
          {t('Feed once a day')}
        </Text>
        <CloseButton onClick={() => historyBackOrReplace(history, Path.VVS_GOTCHI_TEAM)} />
      </Flex>

      <Box mx={3} mb={3}>
        <Text fontWeight="600" color="white" mb={4}>
          {t('Who would you like to feed?')}
        </Text>

        {!individuallyOnly && (
          <Grid gridTemplateColumns="repeat(2, 1fr)" gridGap={2} mb={3}>
            <OptionFlex selected={!feedIndividually} onClick={() => setFeedWholeTeam(true)}>
              <PixelatedImage src="/images/vvsGotchi/quest/socialise.png" height="56px" />
              <Text color="white">
                {t('Whole team (%notFedMoles% Moles)', {
                  notFedMoles: team?.filter((m) => !m.hasFedToday).length ?? 0,
                })}
              </Text>
            </OptionFlex>
            <OptionFlex selected={feedIndividually} onClick={() => setFeedWholeTeam(false)}>
              <PixelatedImage src="/images/vvsGotchi/quest_category/mole_care.png" py="8px" height="56px" />
              <Text color="white">{t('Individual mole')}</Text>
            </OptionFlex>
          </Grid>
        )}

        {(individuallyOnly || feedIndividually) && (
          <Grid gridTemplateColumns="repeat(5, 1fr)" gridGap={2}>
            {team
              ?.map((m) => [m, excludedTokenIds.indexOf(m.tokenId)] as const)
              .map(([mole, idx]) => (
                <Flex
                  key={mole.tokenId}
                  flexDirection="column"
                  alignItems="center"
                  gap="10px"
                  onClick={() => {
                    if (mole.hasFedToday) return
                    if (idx === -1) {
                      setExcludedTokenIds([...excludedTokenIds, mole.tokenId])
                    } else {
                      setExcludedTokenIds([...excludedTokenIds.slice(0, idx), ...excludedTokenIds.slice(idx + 1)])
                    }
                  }}
                >
                  <MoleMiniCard mole={mole} />
                  {mole.hasFedToday ? (
                    <Text mt="2px" fontSize="13px" fontWeight="700" color="grey">
                      {t('Fed')}
                    </Text>
                  ) : (
                    <CheckBox size="24px" checked={idx === -1} />
                  )}
                </Flex>
              ))}
            {Array(5 - (team?.length ?? 0))
              .fill(null)
              .map((_, i) => i)
              .map((key) => (
                <Flex key={key} flexDirection="column" alignItems="center" gap="10px">
                  <MoleMiniCardShadow />
                  <CheckBox size="24px" disabled />
                </Flex>
              ))}
          </Grid>
        )}
      </Box>

      <Flex borderTop="white solid 0.25pt" mx={3} my={2} />

      <Box mx={3} my={3}>
        <Text fontWeight="600" color="white" mb={4}>
          {t('What would you like to feed?')}
        </Text>
        <Grid gridTemplateColumns="repeat(2, 1fr)" gridGap={2} mb={3}>
          {[FoodType.FRIES, FoodType.BROCCOLI].map((ft) => (
            <OptionFlex key={ft} selected={foodType === ft} onClick={() => setFoodType(ft)}>
              <PixelatedImage src={foodTypeToUiDetails[ft].image} height="48px" />

              <Text color="white">{foodTypeToUiDetails[ft].name(t)}</Text>
            </OptionFlex>
          ))}

          {/* <OptionFlex> */}
          {/*   <PixelatedImage src={foodTypeToUiDetails[FoodType.GUACAMOLE].image} height="48px" /> */}

          {/*   <Text color="gray">{foodTypeToUiDetails[FoodType.GUACAMOLE].name(t)}</Text> */}
          {/* </OptionFlex> */}
        </Grid>
      </Box>

      <Box mx={3} my={3}>
        <RowBetween mb={2}>
          <DetailText>{t("Mole's preference")}</DetailText>
          <DetailText>{selectedFoodDetails.molePreference(t)}</DetailText>
        </RowBetween>
        <RowBetween mb={2}>
          <DetailText>{t('Chance of acceptance')}</DetailText>
          <DetailText>{selectedFoodDetails.acceptanceChance * 100}%</DetailText>
        </RowBetween>
        <RowBetween mb={3}>
          <DetailText>{t('Health points added')}</DetailText>
          <DetailText>{selectedFoodDetails.healthPointsToAdd}</DetailText>
        </RowBetween>

        <RowBetween>
          <DetailText>{t('Price')}</DetailText>
          <DetailText>{selectedFoodDetails.price <= 0 ? t('Free') : selectedFoodDetails.price}</DetailText>
        </RowBetween>
      </Box>

      <ScreenFooter>
        <ActionButton
          disabled={!team || (feedIndividually && team.length - fedMoles.length - excludedTokenIds.length <= 0)}
          onClick={async () => {
            try {
              await ensureAuth()
              openFeedingModal()
              ga.gotchiFeedAction(selectedFoodDetails.name(t))
              const { data: feedResultData, errors: feedErrors } = await feedFood({
                variables: {
                  address: account,
                  foodType: foodTypeToGraphqlMap[foodType],
                  tokenIds: team
                    .filter(
                      feedIndividually
                        ? (m) => !m.hasFedToday && excludedTokenIds.indexOf(m.tokenId) === -1
                        : (m) => !m.hasFedToday,
                    )
                    .map((m) => m.tokenId),
                },
              })

              if (feedErrors) throw feedErrors
              await Promise.all([
                refresh(),
                waitPromise(2000), // let mole eat for 2s
              ])

              if (feedResultData.feedFood.acceptFood) {
                if (sePlay) {
                  feedSuccessSound.play()
                }
                toastVVSGotchi(
                  t('Feeding is successful! ( ＾0＾)'),
                  toastTypes.INFO,
                  <PixelatedImage src="/images/vvsGotchi/check.png" width="40px" />,
                  t('%health% health points added', { health: selectedFoodDetails.healthPointsToAdd }),
                )
              } else {
                if (sePlay) {
                  feedFailSound.play()
                }
                toastVVSGotchi(
                  t('Mole rejected the food! (ಥ﹏ಥ)'),
                  toastTypes.INFO,
                  <PixelatedImage src="/images/vvsGotchi/cross.png" width="40px" />,
                  t('Please be patient and try again later'),
                )
              }
              historyBackOrReplace(history, Path.VVS_GOTCHI_TEAM)
            } catch (err) {
              console.error(err)
            } finally {
              closeFeedingModal()
            }
          }}
        >
          {t('Continue')}
        </ActionButton>
      </ScreenFooter>
    </DarkPageContainer>
  )
}

export default FeedMole

export const FeedMoleStackContentFlex = React.forwardRef<HTMLDivElement>((props, ref) => {
  const { team } = useGotchiOwner()

  if (isNoMoleInTeam(team)) return <StackContentFlex ref={ref} {...props} />
  return <FullScreenStackContentFlex ref={ref} {...props} />
})

const isNoMoleInTeam = (team?: Mole[]) => !(team?.length > 0)
